/**
 * Phase 3.1A — Settings card: Click-to-Call personal-phone verification.
 *
 * Renders inside CommunicationSettings. Allows the operator to:
 *   - See the currently verified personal number (or "not verified").
 *   - Verify a new number via SMS OTP (opens VerifyPhoneDialog).
 *   - Replace an existing verified number.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { getAuth } from 'firebase/auth';
import { Phone, ShieldCheck, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import VerifyPhoneDialog from './VerifyPhoneDialog';

const API = process.env.REACT_APP_BACKEND_URL || '';

const formatUS = (phone) => {
  if (!phone) return '';
  const m = String(phone).match(/^\+1(\d{3})(\d{3})(\d{4})$/);
  if (m) return `+1 (${m[1]}) ${m[2]}-${m[3]}`;
  return phone;
};

const PhoneVerificationCard = () => {
  const [loading, setLoading] = useState(true);
  const [verified, setVerified] = useState('');
  const [open, setOpen] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getAuth().currentUser?.getIdToken();
      if (!token) { setLoading(false); return; }
      const res = await fetch(`${API}/api/calls/verified-number`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const j = await res.json();
        setVerified(j.verifiedNumber || '');
      }
    } catch (_) { /* swallow */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <section
      className="bg-white border border-gray-200 rounded-xl p-5 space-y-4"
      data-testid="ctc-verification-card"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <Phone className="w-4 h-4 text-blue-600" />
            Click-to-Call — verify your phone
          </h3>
          <p className="text-xs text-gray-500 mt-1 max-w-xl">
            We'll dial your verified phone first, then bridge you to the
            customer. Customers always see the company KRO number — your
            personal number is never exposed.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-xs text-gray-500" data-testid="ctc-verification-loading">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
        </div>
      ) : verified ? (
        <div className="flex items-center justify-between gap-3 bg-emerald-50 border border-emerald-200 rounded-lg p-3" data-testid="ctc-verification-verified">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-emerald-700" />
            <div>
              <div className="text-xs font-medium text-emerald-900">Verified</div>
              <div className="text-sm text-emerald-800 tabular-nums">{formatUS(verified)}</div>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="text-emerald-700 hover:bg-emerald-100"
            onClick={() => setOpen(true)}
            data-testid="ctc-verification-replace-btn"
          >
            Replace
          </Button>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-3 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="ctc-verification-unverified">
          <div className="text-xs text-amber-900">
            No verified number yet — verify your phone before placing your first call.
          </div>
          <Button
            size="sm"
            onClick={() => setOpen(true)}
            data-testid="ctc-verification-start-btn"
          >
            Verify number
          </Button>
        </div>
      )}

      <VerifyPhoneDialog
        open={open}
        onOpenChange={setOpen}
        initialPhone={verified}
        onVerified={(p) => { setVerified(p); }}
      />
    </section>
  );
};

export default PhoneVerificationCard;
